/**
 * **Terms** — one list of payment terms, everywhere a company states them
 * (`docs/visits-billing-and-proposal.md` §7.4, Board 12's last panel; phone
 * Board 16 cell 3).
 *
 * A `MenuSelect`, 150 wide, whose rows are the contract's, literally:
 * *Same as the customer* (help: the account's terms today), *On receipt*,
 * Net 7 · 10 · 15 · 30 · 45 · 60, and *Custom…*. The closed field says the
 * **effective** terms — `Net 15`, `On receipt` — so a plan deferring to the
 * customer reads what the customer's account says rather than "Same as…".
 * A stored number that is not in the list reads `Net 21` with the tick on
 * *Custom…*.
 *
 * *Custom…* turns the control, in place and at the same height, into the
 * app's `QtyInput` (56 wide, `days`, 0 to 180); Enter or leaving it commits.
 *
 * The open list is cb-12's, drawn by `MenuSelect`'s `ruled` look: 290
 * wide, every row at one indent behind a ✓ column, the chosen row tinted
 * and at 600, a two-line row (*Same as the customer*, *Custom…*) at its
 * ordinary weight with its help under it, and no tile on any row.
 *
 * `allowCustomer={false}` drops the first row, for the places that *are* the
 * customer's terms (the account) or the default behind them (settings).
 */
import { useState } from 'react'
import {
  TERMS_MAX_DAYS, TERMS_NET_DAYS, TERMS_WORDS, termsLabel, type TermsMode,
} from '@rowted/shared'
import { MenuSelect, type MenuOption } from './menu-select.js'
import { QtyInput } from '../builder/rows/line-row.js'

export function TermsSelect({
  termsMode, termsDays, customerDays, allowCustomer = true, disabled, full, onChange,
}: {
  termsMode: TermsMode
  /** The plan's own days, read when `termsMode` is `days`. */
  termsDays: number
  /** The account's terms today — what *Same as the customer* means. */
  customerDays: number
  allowCustomer?: boolean
  disabled?: boolean
  /** Fill the row — the phone's plan sheet, where Terms is a row of its own (Board 16 cell 2). */
  full?: boolean
  onChange: (next: { termsMode: TermsMode; termsDays?: number }) => void
}) {
  const [custom, setCustom] = useState(false)
  const listed = termsDays === 0 || (TERMS_NET_DAYS as readonly number[]).includes(termsDays)

  if (custom) {
    return (
      <span className={full ? 'w-full' : 'w-[150px]'} data-terms-select="">
        <QtyInput
          label="Days"
          width="w-[56px]"
          step={1}
          unit="days"
          value={termsMode === 'days' ? termsDays : customerDays}
          onCommit={(days) => {
            setCustom(false)
            onChange({ termsMode: 'days', termsDays: Math.min(TERMS_MAX_DAYS, Math.max(0, Math.round(days))) })
          }}
        />
      </span>
    )
  }

  const options: MenuOption[] = [
    ...(allowCustomer ? [{
      value: 'customer',
      label: TERMS_WORDS.customer,
      help: TERMS_WORDS.customerHelp(customerDays),
      triggerLabel: termsLabel(customerDays),
    }] : []),
    { value: '0', label: TERMS_WORDS.onReceipt },
    ...TERMS_NET_DAYS.map((days) => ({ value: String(days), label: termsLabel(days) })),
    {
      value: 'custom',
      label: TERMS_WORDS.custom,
      help: TERMS_WORDS.customHelp,
      triggerLabel: termsLabel(termsDays),
    },
  ]

  const value = termsMode === 'customer' && allowCustomer ? 'customer'
    : listed ? String(termsDays) : 'custom'

  return (
    <span className={full ? 'w-full' : 'w-[150px]'} data-terms-select="">
      <MenuSelect
        label={TERMS_WORDS.label}
        size="field"
        value={value}
        disabled={disabled}
        options={options}
        minWidth={290}
        noSearch
        ruled
        sheet={{ title: TERMS_WORDS.sheetTitle, sub: TERMS_WORDS.sheetSub }}
        onChange={(next) => {
          if (next === 'custom') { setCustom(true); return }
          if (next === null || next === value) return
          if (next === 'customer') onChange({ termsMode: 'customer' })
          else onChange({ termsMode: 'days', termsDays: Number(next) })
        }}
      />
    </span>
  )
}
